import { Component, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContactFormModalStore } from './contact-form-modal.store';
import { PostSendContactFormRequest } from '../../Models/Requests/PostSendContactFormRequest';
import { LoadingSpinnerSelectComponent } from '../loading-spinner-select/loading-spinner-select.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

@Component({
  selector: 'debtor-panel-contact-form-modal',
  templateUrl: './contact-form-modal.component.html',
  styleUrls: ['./contact-form-modal.component.scss'],
  providers: [TranslateService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    LoadingSpinnerSelectComponent,
    LoadingSpinnerComponent,
  ],
})
export class ContactFormModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly dialogRef = inject(MatDialogRef<ContactFormModalComponent>);
  private mbToBytesRation = 1024 * 1024;
  protected readonly store = inject(ContactFormModalStore);
  protected selectedFileName: string | null = null;
  protected messageMaxLength = this.store.messageMaxLength();
  protected messageMaxLengthNumber = Number(this.messageMaxLength);
  protected readonly contactForm: FormGroup = this.fb.group({
    topic: ['', Validators.required],
    message: ['', [Validators.required, Validators.maxLength(this.messageMaxLengthNumber)]],
    attachment: [null],
  });

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscPressed(event: KeyboardEvent): void {
    if (this.contactForm.dirty) {
      this.translateService.get('contact-form.close-modal-info').subscribe((translatedText: string) => {
        const confirmed = confirm(translatedText);
        if (confirmed) {
          this.dialogRef.close();
        }
      });
    }
    else {
      this.dialogRef.close();
    }
  }

  protected onFileSelected(event: Event): void {
    const allowedExtensions = ['pdf', 'jpeg', 'png', 'tiff', 'tif', 'bmp'];
    const target = event.target as HTMLInputElement;
    const files = target.files as FileList;
    const file = files[0];
    if (file) {
      const fileName = file.name;
      if(fileName.match(this.store.notAllowedRegEx()) || fileName.length > this.store.maxNameLength()) {
        this.translateService.get('contact-form.invalid-file-name').subscribe((translatedText: string) => {
          alert(translatedText);
        });
        return;
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        this.translateService.get('contact-form.invalid-file-extension', { formats: allowedExtensions.join(', ') }).subscribe((translatedText: string) => {
          alert(translatedText);
        });
        return;
      }

      if (file.size > (this.store.maxFileSize() * this.mbToBytesRation)) {
        this.translateService.get('contact-form.max-file-size-info', { amount: this.store.maxFileSize()}).subscribe((translatedText: string) => {
          const alertText = translatedText;
          alert(alertText);
        });
        return;
      }
      this.contactForm.patchValue({
        attachment: file,
      });
      this.selectedFileName = file.name;
    }
  }

  protected onCloseClick(): void {
    if (this.contactForm.dirty) {
      this.translateService.get('contact-form.close-modal-info').subscribe((translatedText: string) => {
        const confirmed = confirm(translatedText);
        if (confirmed) {
          this.dialogRef.close();
        }
      });
    }
    else {
      this.dialogRef.close();
    }
  }

  protected removeFile(): void {
    this.contactForm.patchValue({
      attachment: null,
    });
    this.selectedFileName = null;

    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  protected onSubmit(): void {
    const form : PostSendContactFormRequest = {
      subjectIdHash: this.contactForm.get('topic')?.value,
      message: this.contactForm.get('message')?.value,
      file: this.contactForm.get('attachment')?.value,
    };

    this.store.sendForm(form);
    this.dialogRef.close();
  }
}